import type {
  EgressPathSnapshotRow,
  EgressPathTargetObservation,
  EgressTargetDecision,
} from '@nadoutong/contracts';
import type { MatrixNode } from './matrix.ts';

export interface TargetCandidate {
  readonly pathId: string;
  readonly observation: EgressPathTargetObservation;
}

export interface UpstreamDecisionBranch {
  readonly upstreamId: string;
  readonly upstreamName: string | null;
  readonly upstreamRegion: string | null;
  readonly selectedPathId: string;
  readonly decision: EgressTargetDecision | null;
  readonly selected: TargetCandidate | null;
  readonly candidates: readonly TargetCandidate[];
}

export interface NodeDecisionTree {
  readonly node: MatrixNode;
  readonly upstreams: readonly UpstreamDecisionBranch[];
}

export function buildDecisionForest(
  nodes: readonly MatrixNode[],
  snapshots: readonly EgressPathSnapshotRow[],
): readonly NodeDecisionTree[] {
  const snapshotsByNode = new Map<string, EgressPathSnapshotRow[]>();
  for (const row of snapshots) {
    const existing = snapshotsByNode.get(row.node_id) ?? [];
    snapshotsByNode.set(row.node_id, [...existing, row]);
  }
  return nodes.map((node) => ({
    node,
    upstreams: branchesForNode(snapshotsByNode.get(node.nodeId) ?? []),
  }));
}

function branchesForNode(paths: readonly EgressPathSnapshotRow[]): readonly UpstreamDecisionBranch[] {
  const byUpstream = new Map<string, TargetCandidate[]>();
  for (const path of paths) {
    for (const observation of path.target_observations) {
      const list = byUpstream.get(observation.upstream_id) ?? [];
      list.push({ pathId: path.path_id, observation });
      byUpstream.set(observation.upstream_id, list);
    }
  }
  const branches: UpstreamDecisionBranch[] = [];
  for (const [upstreamId, candidates] of byUpstream) {
    const first = candidates[0]?.observation;
    const decision = candidates.find((row) => row.observation.decision !== undefined)?.observation.decision
      ?? null;
    const selectedPathId = decision?.selected_path_id
      ?? candidates.find((row) => row.observation.selected)?.pathId
      ?? 'direct';
    const selected = candidates.find((row) => row.pathId === selectedPathId) ?? null;
    branches.push({
      upstreamId,
      upstreamName: first?.upstream_name ?? null,
      upstreamRegion: first?.upstream_region ?? null,
      selectedPathId,
      decision,
      selected,
      candidates,
    });
  }
  return branches.sort((left, right) => {
    const leftName = left.upstreamName ?? left.upstreamId;
    const rightName = right.upstreamName ?? right.upstreamId;
    return leftName.localeCompare(rightName);
  });
}
